import { z } from "zod";

/** Ids are lowercase kebab-case so they are safe in URLs and file names. */
export const idSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "id must be lowercase kebab-case");

/** Media paths are relative to the media base (default `/media/`). Never absolute, never `..`. */
export const mediaPath = z
  .string()
  .min(1)
  .refine((p) => !p.startsWith("/") && !p.includes("..") && !/^[a-z]+:/.test(p), "media path must be relative");

const percent = z.number().min(0).max(100);

export const rectSchema = z.object({ x: percent, y: percent, w: percent, h: percent });

// ---------------------------------------------------------------- dialogue
export const dialogueActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("showProduct"), productId: idSchema }),
  z.object({ type: z.literal("addToBasket"), productId: idSchema }),
  z.object({ type: z.literal("goToLocation"), locationId: idSchema }),
  z.object({ type: z.literal("goToShop"), shopId: idSchema }),
  z.object({ type: z.literal("openDirectory") }),
  z.object({ type: z.literal("openBasket") })
]);

export const dialogueOptionSchema = z.object({
  text: z.string().min(1),
  /** Next node; omit to end the conversation after the action. */
  next: z.string().optional(),
  action: dialogueActionSchema.optional()
});

export const dialogueNodeSchema = z.object({
  say: z.string().min(1),
  /** Line used instead of `say` while anything is archived (when `say` mentions archived areas). */
  sayTrimmed: z.string().min(1).optional(),
  options: z.array(dialogueOptionSchema).default([])
});

export const dialogueScriptSchema = z.object({
  start: z.string(),
  nodes: z.record(z.string(), dialogueNodeSchema)
});

// -------------------------------------------------------------- characters
export const personaSchema = z.object({
  /** One-line description used by the art prompts AND (later) the AI system prompt. */
  summary: z.string().min(1),
  voice: z.string().min(1),
  traits: z.array(z.string()).default([])
});

/** ElevenLabs voice for a character (public library/voice id, not a secret). Settings default to the worker's. */
export const aiVoiceSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9]{12,40}$/, "voice id must be an ElevenLabs voice_id"),
  name: z.string().min(1),
  stability: z.number().min(0).max(1).optional(),
  similarity: z.number().min(0).max(1).optional(),
  style: z.number().min(0).max(1).optional()
});

/**
 * AI chat persona for a character. Presence of this block turns on the free-text AI chat and worker voice for them.
 * Everything here is content: the worker may only state facts that come from the content (their location, shop,
 * products, scripted lines, the building directory) plus `facts`. See docs/ADDING-AREAS.md.
 */
export const aiSchema = z.object({
  /** Who they are in the building. 1-2 sentences, no invented numbers, prices, hours or other people. */
  backstory: z.string().min(1),
  /** How they speak (rhythm, accent, verbal habits). */
  style: z.string().min(1),
  /** Topics they may talk about. */
  knows: z.array(z.string().min(1)).min(1),
  /** Extra topics this character steers away from (on top of the shared off-limits list). */
  avoids: z.array(z.string().min(1)).default([]),
  /** Extra authored facts the character may state (e.g. what their space looks like). Never invent prices, hours or people. */
  facts: z.array(z.string().min(1)).default([]),
  /** Short in-character line used whenever a message is boxed out (input filter, output filter, injection). Redirects to what they can help with. */
  deflection: z.string().min(1).max(240),
  /** Opening line. Defaults to the first scripted line. */
  greeting: z.string().min(1).optional(),
  /**
   * Location ids this character may take the guest to (travel tool). Validated against the loaded manifest and
   * skipped while archived. A character whose location has a `menu` hotspot (the lift) guides to its items automatically.
   */
  canGuide: z.array(idSchema).default([]),
  voice: aiVoiceSchema
});

export const characterSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  role: z.enum(["assistant", "cashier", "concierge", "shopper", "attendant"]),
  locationId: idSchema,
  shopId: idSchema.optional(),
  persona: personaSchema,
  /** Base path (no extension) of a transparent portrait: `<sprite>.png`, optional `<sprite>.webm` idle loop. */
  sprite: mediaPath,
  /**
   * Optional full-frame "living still" clips (16:9, same framing as the location still): `<idle>.mp4|.webm` loops
   * while you are there (first frame = last frame = the still), `<talk>.mp4|.webm` plays once when you talk to them
   * and also starts and ends on the still. Optional `<enter>` plays once on arrival, starting on the location still
   * (person walks in) and ending on the idle frame. Characters sharing a location share the idle/enter clips. When set, the sprite is not drawn; the button is a hit area at `position`.
   */
  live: z.object({ idle: mediaPath, talk: mediaPath, enter: mediaPath.optional() }).optional(),
  /** x = centre, y = feet, as % of the stage; height as % of stage height. */
  position: z.object({ x: percent, y: percent, height: z.number().min(10).max(100), flip: z.boolean().default(false) }),
  script: dialogueScriptSchema,
  /** AI chat persona + voice. Optional; characters without it keep the scripted dialogue. */
  ai: aiSchema.optional()
});

// ---------------------------------------------------------------- products
export const productSchema = z.object({
  id: idSchema,
  shopId: idSchema,
  sku: z.string().min(1),
  name: z.string().min(1),
  brand: z.string().min(1),
  category: z.string().min(1),
  /** Integer pence (GBP), avoids float errors. Omit for "price on request" items (no basket). */
  pricePence: z.number().int().positive().optional(),
  description: z.string().min(1),
  material: z.string().optional(),
  colours: z.array(z.object({ name: z.string(), hex: z.string().regex(/^#[0-9a-fA-F]{6}$/) })).min(1),
  sizes: z.array(z.string()).min(1),
  /** Base path of a 4:5 image: `<image>.jpg`. */
  image: mediaPath
});

// --------------------------------------------------------------- locations
/** Which way an arrow hotspot points: in the direction of travel as seen on the still. */
export const hotspotDirections = ["up", "down", "left", "right", "forward", "back"] as const;
export const hotspotDirectionSchema = z.enum(hotspotDirections);
export type HotspotDirection = z.infer<typeof hotspotDirectionSchema>;

/** Icons a hotspot can show (drawn in src/ui/icons.ts). Optional: a default is inferred from the kind / destination. */
export const hotspotIcons = ["lift", "stairs", "door", "exit", "home", "shop", "bag", "till", "table", "telescope", "chair", "stand", "sofa", "view", "leaf", "car", "trophy", "flag", "gear", "key", "pencil", "sliders", "info", "map", "tag"] as const;
export const hotspotIconSchema = z.enum(hotspotIcons);
export type HotspotIcon = z.infer<typeof hotspotIconSchema>;

export const hotspotSchema = z.discriminatedUnion("kind", [
  z.object({
    id: idSchema,
    kind: z.literal("navigate"),
    label: z.string(),
    rect: rectSchema, icon: hotspotIconSchema.optional(),
    to: idSchema,
    /**
     * Arrow direction (`up|down|left|right|forward|back`). Optional: when omitted it is inferred from the rect
     * position (see `inferDirection`). `forward` = into the scene, `back` = out of the scene, towards the camera.
     */
    direction: hotspotDirectionSchema.optional()
  }),
  z.object({ id: idSchema, kind: z.literal("product"), label: z.string(), rect: rectSchema, icon: hotspotIconSchema.optional(), productId: idSchema }),
  z.object({ id: idSchema, kind: z.literal("directory"), label: z.string(), rect: rectSchema, icon: hotspotIconSchema.optional() }),
  z.object({ id: idSchema, kind: z.literal("checkout"), label: z.string(), rect: rectSchema, icon: hotspotIconSchema.optional() }),
  /** Opens a car configurator panel. `src` is a content file (relative to `content/`), see `src/configurator/schema.ts`. */
  z.object({ id: idSchema, kind: z.literal("configurator"), label: z.string(), rect: rectSchema, icon: hotspotIconSchema.optional(), src: z.string().min(1).regex(/^[a-z0-9][a-z0-9/_-]*\.json$/, "src must be a relative .json content path") }),
  z.object({ id: idSchema, kind: z.literal("info"), label: z.string(), rect: rectSchema, icon: hotspotIconSchema.optional(), text: z.string() }),
  /**
   * Opens an interactive telescope: a wide panorama (`<panorama>.jpg`) seen through a round eyepiece, panned by drag,
   * arrow keys or buttons. Centring `target` (x/y = % of the panorama, r = tolerance as % of the panorama width)
   * shows `found.text` and the promo `found.code` of shop `found.shopId`. The code must be a promo of that shop.
   */
  z.object({
    id: idSchema,
    kind: z.literal("telescope"),
    label: z.string(),
    rect: rectSchema, icon: hotspotIconSchema.optional(),
    title: z.string().min(1),
    panorama: mediaPath,
    alt: z.string().min(1),
    /** Where the eyepiece starts (centre, % of the panorama). */
    start: z.object({ x: percent, y: percent }),
    target: z.object({ x: percent, y: percent, r: z.number().min(1).max(30) }),
    found: z.object({ code: z.string().regex(/^[A-Z0-9]{4,16}$/), shopId: idSchema, text: z.string().min(1) })
  }),
  /** Opens the retailer floor planner (rooms, assistants, things to explore; tier limits). See docs/RETAILER-CONFIGURATOR.md. */
  z.object({ id: idSchema, kind: z.literal("retailer-config"), label: z.string(), rect: rectSchema, icon: hotspotIconSchema.optional(), title: z.string().min(1) }),
  /**
   * A small hidden clue (a tucked-away card): opening it shows `text` and the demo discount `code` of shop `shopId`.
   * Drawn very faintly so it has to be looked for. The code must be a promo of that shop. Never put codes in AI facts.
   */
  z.object({
    id: idSchema,
    kind: z.literal("clue"),
    label: z.string(),
    rect: rectSchema, icon: hotspotIconSchema.optional(),
    title: z.string().min(1),
    text: z.string().min(1),
    code: z.string().regex(/^[A-Z0-9]{4,16}$/),
    shopId: idSchema
  }),
  /** One touch point that opens a panel of large destination buttons (e.g. the lift's floor panel). */
  z.object({
    id: idSchema,
    kind: z.literal("menu"),
    label: z.string(),
    rect: rectSchema, icon: hotspotIconSchema.optional(),
    title: z.string().min(1),
    items: z.array(z.object({ badge: z.string().optional(), label: z.string().min(1), to: idSchema })).min(1)
  })
]);

export const locationKinds = ["atrium", "arcade", "gallery", "lift", "escalator", "exterior", "floor", "till", "fitting"] as const;

export const locationSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  kind: z.enum(locationKinds),
  districtId: idSchema,
  shopId: idSchema.optional(),
  /** Base path (no extension) of the still: `<still>.jpg`. 16:9, 1920x1080. */
  still: mediaPath,
  alt: z.string().min(1),
  /** Scene brief used by the shot list / prompt generator. Not shown to shoppers. */
  brief: z.string().min(1),
  hotspots: z.array(hotspotSchema).default([]),
  /** Optional notice always shown on this location (e.g. "Concept for discussion - not affiliated"). */
  notice: z.string().min(1).max(160).optional()
});

// ------------------------------------------------------------- transitions
export const transitionSchema = z.object({
  id: idSchema,
  from: idSchema,
  to: idSchema,
  /** Base path (no extension): `<video>.mp4` (+ `<video>.webm`). Reverse clip, if derived: `<video>.rev.mp4`. */
  video: mediaPath,
  /** Optional poster; defaults to the origin still. */
  poster: mediaPath.optional(),
  durationSec: z.number().min(2).max(12),
  /**
   * When true the engine derives the `to -> from` edge, which plays `<video>.rev.*` (the
   * same clip reversed, so coming back out always mirrors going in). Never declare a
   * separate opposite-direction transition: the content lint rejects it.
   */
  reversible: z.boolean().default(true),
  /** Camera / motion brief for the image-to-video prompt. */
  motion: z.string().min(1),
  /** Label for the walk (screen reader + HUD), e.g. "Walking to the Promenade". */
  label: z.string().min(1),
  /** Optional label for the way back, when the derived reverse clip plays (e.g. "Back to the Design Studio"). */
  reverseLabel: z.string().min(1).optional()
});

// -------------------------------------------------------------------- shops
export const promoSchema = z.object({
  /** Upper-case code the shopper types, e.g. TERRACE15. */
  code: z.string().regex(/^[A-Z0-9]{4,16}$/),
  percentOff: z.number().int().min(1).max(50),
  /** Shown on the basket discount row, e.g. "Terrace telescope offer". */
  label: z.string().min(1)
});

export const shopSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  category: z.string().min(1),
  districtId: idSchema,
  tagline: z.string().min(1),
  description: z.string().min(1),
  /** Where "take me to this shop" ends. Defaults to the shop's exterior. */
  entryLocationId: idSchema,
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /** Art direction notes layered over the district style. */
  style: z.string().min(1),
  /** Demo discount codes for this shop's priced items (see Basket.applyCode). Never put codes in AI facts. */
  promos: z.array(promoSchema).optional()
});

export const districtSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  level: z.string(),
  palette: z.array(z.string().regex(/^#[0-9a-fA-F]{6}$/)).min(3),
  lighting: z.string().min(1),
  materials: z.string().min(1)
});

// -------------------------------------------------------------------- files
export const mallFileSchema = z.object({
  schemaVersion: z.literal(1),
  mall: z.object({
    id: idSchema,
    name: z.string(),
    tagline: z.string(),
    startLocationId: idSchema,
    directoryLocationId: idSchema.optional()
  }),
  districts: z.array(districtSchema).min(1),
  /** Paths (relative to content/) of shop files, loaded lazily in the real app later. */
  shopFiles: z.array(z.string()),
  /**
   * Soft delete. Archived shops (with all their locations, products and characters) and archived
   * mall locations stay in the content files but are stripped at load time, along with every
   * hotspot, transition, character and dialogue option that points at them. Remove an id to restore.
   * See docs/ARCHIVE.md.
   */
  archived: z.object({ locations: z.array(idSchema).default([]), shops: z.array(idSchema).default([]) }).default({ locations: [], shops: [] }),
  locations: z.array(locationSchema),
  transitions: z.array(transitionSchema),
  characters: z.array(characterSchema)
});

export const shopFileSchema = z.object({
  schemaVersion: z.literal(1),
  shop: shopSchema,
  locations: z.array(locationSchema),
  transitions: z.array(transitionSchema),
  characters: z.array(characterSchema),
  products: z.array(productSchema)
});

/** Merged runtime manifest. */
export const manifestSchema = z.object({
  schemaVersion: z.literal(1),
  /** Names of shops/locations removed by the archive (set by applyArchive); lets AI grounding skip facts that mention them. */
  archivedNames: z.array(z.string()).optional(),
  mall: mallFileSchema.shape.mall,
  districts: z.array(districtSchema),
  shops: z.array(shopSchema),
  locations: z.array(locationSchema),
  transitions: z.array(transitionSchema),
  characters: z.array(characterSchema),
  products: z.array(productSchema)
});

export type DialogueAction = z.infer<typeof dialogueActionSchema>;
export type DialogueScript = z.infer<typeof dialogueScriptSchema>;
export type DialogueNode = z.infer<typeof dialogueNodeSchema>;
export type Character = z.infer<typeof characterSchema>;
export type CharacterAi = z.infer<typeof aiSchema>;
export type Product = z.infer<typeof productSchema>;
export type Hotspot = z.infer<typeof hotspotSchema>;
export type Location = z.infer<typeof locationSchema>;
export type Transition = z.infer<typeof transitionSchema>;
export type Shop = z.infer<typeof shopSchema>;
export type Promo = z.infer<typeof promoSchema>;
export type District = z.infer<typeof districtSchema>;
export type MallFile = z.infer<typeof mallFileSchema>;
export type ShopFile = z.infer<typeof shopFileSchema>;
export type Manifest = z.infer<typeof manifestSchema>;
